export enum DayOfWeek {
  MONDAY = "monday",
  TUESDAY = "tuesday",
  WEDNESDAY = "wednesday",
  THURSDAY = "thursday",
  FRIDAY = "friday",
  SATURDAY = "saturday",
  SUNDAY = "sunday",
}

export enum JobPostDecisionReason {
  Specialty = "specialty",
  Pay = "pay",
  Location = "location",
  Schedule = "schedule",
  Facility = "facility",
  Responsibilities = "responsibilities",
  Qualifications = "qualifications",
  Other = "other",
}

/** How well the AI judged a job to fit a provider, strongest first. */
export enum MatchLikelihood {
  High = "high",
  Medium = "medium",
  Low = "low",
  NoMatch = "no-match",
}

export enum WageFrequency {
  OneTime = "one_time",
  Hourly = "hourly",
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
  Yearly = "yearly",
}

export enum CredentialField {
  Name = 1,
  NpiNumber = 3,
  WorkExperience = 6,
  Dea = 11,
  Specialty = 15,
  Address = 19,
  StateMedicalLicenses = 20,
  HealthcareProfessionalType = 22,
  ImlcLicense = 25,
}

export enum JobLocationType {
  InPerson = "in_person",
  Remote = "remote",
  Hybrid = "hybrid",
}

export enum JobSearchStatus {
  ActivelyLooking = 1,
  PassivelyLooking = 2,
  NotLooking = 3,
}

export enum JobDuration {
  PerDiemPRN = 1,
  Contract = 2,
  FullTime = 3,
  PartTime = 4,
  Consulting = 5,
  Locums = 6,
}

export enum EmploymentType {
  FullTime = "full_time",
  PartTime = "part_time",
  PerDiemPRN = "per_diem_prn",
  Contract = "contract",
  Consulting = "consulting",
  Locums = "locums",
}

export enum JobPostProviderNotificationFrequency {
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
  Never = "never",
}

export enum JobPostVisibility {
  Public = "public",
  DirectLink = "direct_link",
}

/** What a link to a job post finds: an open post, one that was posted and has
 * since closed, or nothing a visitor may see. */
export enum JobPostAvailability {
  Open = "open",
  Closed = "closed",
  NotFound = "not-found",
}

export enum Platform {
  Web = "web",
  IOS = "ios",
  Android = "android",
}

export enum UserCredentialsFieldValueSource {
  App = "app",
  Baton = "baton",
}

export enum ProviderCredentialEditRequestStatus {
  Pending = "pending",
  Approved = "approved",
  Denied = "denied",
  Revoked = "revoked",
  Canceled = "canceled",
}

export enum AffiliationInvitationStatus {
  Draft = "draft",
  Invited = "invited",
  Started = "started",
  Onboarded = "onboarded",
  Linked = "linked",
  Expired = "expired",
  Removed = "removed",
}

export enum FacilityType {
  Hospital = "Hospital",
  MedicalSpa = "Medical Spa",
  NursingHome = "Nursing Home",
  RehabCenter = "Rehab Center",
  UrgentCare = "Urgent Care",
  Telemedicine = "Telemedicine",
  DialysisCenter = "Dialysis Center",
  ConciergeMedicine = "Concierge Medicine",
  IVInfusionCenter = "IV Infusion Center",
  SurgeryCenterOBL = "Surgery Center OBL",
  OutpatientClinic = "Outpatient Clinic",
  Consulting = "Consulting",
  Other = "Other/Unspecified",
}

export enum PracticeSetting {
  Hospital = "hospital",
  OutpatientClinic = "outpatient_clinic",
  LongTermCare = "long_term_care",
  SurgicalCenter = "surgical_center",
  SpecialtyCenter = "specialty_center",
  RemoteVirtual = "remote_virtual",
  HomeHealth = "home_health",
}

export enum Permission {
  // Existing permissions (legacy)
  ViewLeads = "view-leads",
  EditLeads = "edit-leads",

  // BU-scoped permissions
  // When assigned to BU role: applies to that BU only
  // When assigned to global role: applies to ALL BUs
  ManageJobPosts = "manage-job-posts",
  ViewJobPosts = "view-job-posts",
  ManageChat = "manage-chat",
  ViewChat = "view-chat",
  ManageJobOffers = "manage-job-offers",
  ViewJobOffers = "view-job-offers",
  ApproveTimesheets = "approve-timesheets",
  ViewTimesheets = "view-timesheets",
  ManageInvoices = "manage-invoices",
  ViewInvoices = "view-invoices",
  ManageProviderPools = "manage-provider-pools",
  ViewProviderPools = "view-provider-pools",
  BypassProviderPoolRestrictions = "bypass-provider-pool-restrictions",
  EditProviderCredentials = "edit-provider-credentials",
  ViewProviderCredentials = "view-provider-credentials",
  ManageTeamMembers = "manage-team-members",
  ViewTeamMembers = "view-team-members",
  ManageAffiliations = "manage-affiliations",
  ManageBuSettings = "manage-bu-settings",
  ProviderSearch = "provider-search",

  // Global-only permissions (can only be assigned to global roles)
  AppManageOrganizations = "app-manage-organizations",
  AppManageUsers = "app-manage-users",
  AppViewAnalytics = "app-view-analytics",
  AppManageProviderPayouts = "app-manage-provider-payouts",
  AppChargeOrganizationInvoices = "app-charge-organization-invoices",
  AppImpersonateUsers = "app-impersonate-users",
}

export enum PermissionScope {
  Global = "global",
  BusinessUnit = "business-unit",
}

export enum RoleScope {
  Global = "global",
  BusinessUnit = "business-unit",
}

export enum JobOfferStatus {
  WaitingForProvider = "waiting_for_provider",
  Accepted = "accepted",
  Canceled = "canceled",
  Expired = "expired",
}

export enum JobOfferShiftStatus {
  Scheduled = "scheduled",
  Disputed = "disputed",
  Confirmed = "confirmed",
  NoShow = "no_show",
  Canceled = "canceled",
}

export enum JobOfferShiftSource {
  Scheduled = "scheduled",
  ProviderRequested = "provider_requested",
  Proposal = "proposal",
}

export enum ProviderShiftIssueType {
  UnableToAttend = "unable_to_attend",
  IncorrectDateOrTime = "incorrect_date_or_time",
  Other = "other",
}

export enum JobOfferEditProposalStatus {
  Pending = "pending",
  Accepted = "accepted",
  Declined = "declined",
  Withdrawn = "withdrawn",
  Expired = "expired",
}

export enum JobOfferEditProposalInitiatorRole {
  Provider = "provider",
  Organization = "organization",
}

export enum TimesheetEntryBaseWageType {
  Regular = "regular",
  OnCall = "on_call",
  Weekend = "weekend",
  Holiday = "holiday",
  OnCallCallback = "on_call_callback",
}

export enum JobOfferShiftTimeEntryStatus {
  PendingOrgReview = "pending_org_review",
  Approved = "approved",
  Rejected = "rejected",
}

export enum ExpenseCategory {
  Travel = "travel",
  Meals = "meals",
  Lodging = "lodging",
  Parking = "parking",
  Mileage = "mileage",
  Supplies = "supplies",
  Other = "other",
}

export enum ExpenseStatus {
  PendingOrgReview = "pending_org_review",
  Approved = "approved",
  Rejected = "rejected",
}

export enum ProviderPayableItemSourceType {
  TimeEntry = "time_entry",
  Expense = "expense",
}

export enum ProviderPayableItemStatus {
  Ready = "ready",
  Paid = "paid",
  Void = "void",
}

export enum ProviderPayoutMode {
  Automatic = "automatic",
  Manual = "manual",
}

export enum ProviderPayoutStatus {
  Processing = "processing",
  Transferred = "transferred",
  PayoutPending = "payout_pending",
  Paid = "paid",
  Failed = "failed",
  Canceled = "canceled",
  Reversed = "reversed",
}

export enum ProviderPayoutAttemptStatus {
  Pending = "pending",
  Succeeded = "succeeded",
  Failed = "failed",
}

export enum PaymentService {
  Stripe = "stripe",
  Manual = "manual",
}

/** Stripe payment method types a business unit may save for invoices. */
export enum BillingPaymentMethodType {
  Card = "card",
  UsBankAccount = "us_bank_account",
}

/** Mirrors the processor's invoice status; `draft` also means not yet exported. */
export enum InvoiceStatus {
  Draft = "draft",
  Open = "open",
  Paid = "paid",
  Void = "void",
  Uncollectible = "uncollectible",
}

export enum InvoiceLineKind {
  BaseFee = "base-fee",
  Usage = "usage",
  WagePassThrough = "wage-pass-through",
}

/**
 * When a plan's base fee is invoiced. In arrears, one invoice per cycle the
 * day after it ends. In advance, the invoice generated on a cycle's first day
 * carries that cycle's base fee plus the previous cycle's usage; an ended plan
 * gets a usage-only closing invoice.
 */
export enum BaseFeeTiming {
  InArrears = "in-arrears",
  InAdvance = "in-advance",
}

export enum InvoiceKind {
  Cycle = "cycle",
  Closing = "closing",
}

/**
 * How an issued invoice is collected. A processor invoice is a Stripe
 * Invoice paid on terms; a direct charge is a PaymentIntent against the
 * unit's saved method with no Stripe Invoice and no invoicing fee.
 */
export enum InvoiceCollectionPath {
  ProcessorInvoice = "processor-invoice",
  DirectCharge = "direct-charge",
}

export enum BillingOperationKind {
  Export = "export",
  Finalize = "finalize",
  Charge = "charge",
}

export enum BillingOperationStatus {
  Pending = "pending",
  Succeeded = "succeeded",
  Failed = "failed",
}

export enum InvoiceEventKind {
  Generated = "generated",
  Issued = "issued",
  Emailed = "emailed",
  Resent = "resent",
  ChargeSubmitted = "charge-submitted",
  ChargeDeclined = "charge-declined",
  AuthenticationRequired = "authentication-required",
  Paid = "paid",
  Refunded = "refunded",
  Voided = "voided",
  ReceiptSent = "receipt-sent",
}

/** Processor invoice events that post to Slack. */
export enum InvoiceSlackEventKind {
  Finalized = "finalized",
  Paid = "paid",
  PaymentFailed = "payment-failed",
  Refunded = "refunded",
}

export enum ScheduleType {
  Regular = "regular",
  OnCall = "on_call",
}

export enum HiringProcessInstanceStatus {
  NotStarted = "not_started",
  InProgress = "in_progress",
  Completed = "completed",
  Cancelled = "cancelled",
}

export enum HiringProcessItemStatus {
  NotStarted = "not_started",
  InProgress = "in_progress",
  Completed = "completed",
  Skipped = "skipped",
  NotApplicable = "not_applicable",
}

/**
 * Single source of truth for "this item's progress is terminal" — done OR
 * intentionally not required. Several callers gate on this (completion checks,
 * the reminder campaign's actionable-pending count). Import it instead of
 * re-listing the trio so a new terminal status can't be added to one site and
 * missed in another.
 */
export const TERMINAL_HIRING_PROCESS_ITEM_STATUSES =
  new Set<HiringProcessItemStatus>([
    HiringProcessItemStatus.Completed,
    HiringProcessItemStatus.Skipped,
    HiringProcessItemStatus.NotApplicable,
  ]);

export const isTerminalHiringProcessItemStatus = (
  status: HiringProcessItemStatus | undefined,
): boolean =>
  status !== undefined && TERMINAL_HIRING_PROCESS_ITEM_STATUSES.has(status);

export enum HiringProcessActionRequiredBy {
  Organization = "organization",
  Candidate = "candidate",
}

export enum HiringProcessAssigneeRole {
  Organization = "organization",
  Provider = "provider",
}

export enum HiringProcessItemType {
  Task = "task",
  Form = "form",
  DocumentShare = "document_share",
  /**
   * Per-candidate file/link slot, filled at execution time. Bidirectional —
   * direction depends on `assigneeRole`:
   *   - provider-assigned → the CANDIDATE uploads (e.g. a license/credential);
   *   - organization-assigned → the ORG sends a per-candidate file/link.
   * Template defines only the slot (title/description); files/links live on the
   * candidate's progress row.
   */
  DocumentUpload = "document_upload",
  /**
   * A per-candidate document the org sets up at runtime for SIGNING: the org
   * either uploads a PDF (mapping fields) or picks a reusable two-party form,
   * fills the candidate-specific org fields, then the candidate signs the rest.
   * The chosen form is stored on the candidate's progress row (`fileFormId`).
   */
  DocumentSigning = "document_signing",
}

export enum FormFieldType {
  Text = "text",
  Textarea = "textarea",
  Select = "select",
  MultiSelect = "multi_select",
  FileUpload = "file_upload",
  Date = "date",
  Checkbox = "checkbox",
  /** Captured as a base64 PNG image; filled into a provider image field. */
  Signature = "signature",
}

/**
 * Pluggable backend that fills a physical PDF. The data model is agnostic to
 * the specific vendor — only the `pdf-form` service layer knows the vendor
 * details. Add a value here when integrating another provider (or self-host).
 */
export enum FileFillVendor {
  Docuseal = "docuseal",
}

/** Authoring lifecycle of a PDF form's field mapping. */
export enum FileMappingStatus {
  Draft = "draft",
  Ready = "ready",
  Error = "error",
}

/** Fill lifecycle of a single PDF submission: created, then filled on finalize. */
export enum FileFillStatus {
  Pending = "pending",
  Filled = "filled",
}

/**
 * Which party fills/signs a file-form field (and a signer on a submission).
 * "candidate" = the person being hired; "organization" = org-side signer.
 */
export enum FileSignerRole {
  Organization = "organization",
  Candidate = "candidate",
}

/** Per-party signing state on a multi-party file submission. */
export enum FileSignerStatus {
  Pending = "pending",
  Completed = "completed",
}

/**
 * Sequential signing order for a multi-party file form. Hiring-process
 * sessions only — chat form assignments always sign org-first (the org
 * completes its part before the assignment is sent).
 */
export enum FileSigningOrder {
  OrgFirst = "org_first",
  CandidateFirst = "candidate_first",
}

/**
 * Whether a form is a reusable template (built once, listed, picked at setup) or
 * an instance form created at runtime for one specific candidate (hidden from
 * the global forms list).
 */
export enum FormScope {
  Reusable = "reusable",
  Instance = "instance",
}

/**
 * Lifecycle of an ad-hoc form assignment — a form sent directly to a provider
 * (via chat), outside any hiring process. `draft` is org-side prep (the org
 * signs its part before sending): no chat message exists yet and the
 * recipient can't see the assignment.
 */
export enum FormAssignmentStatus {
  Draft = "draft",
  Sent = "sent",
  Completed = "completed",
  Canceled = "canceled",
  Rejected = "rejected",
}

export enum JobPostRequirementType {
  StateLicense = "state_license",
  ProfessionalType = "professional_type",
  Specialty = "specialty",
  /**
   * Narrative requirement shown to candidates and fed to the AI matcher.
   * Never gates, never produces lapsed-requirement rows — purely
   * descriptive. The evaluator returns satisfied=true for every free_text
   * row so they're invisible to the deterministic gate.
   *
   * Free-text rows are dual-written to `jobPost.requiredQualifications`
   * by `setRequirements` so existing readers (AI evaluator, public job
   * detail) keep working without migration.
   */
  FreeText = "free_text",
}

export enum JobPostRequirementOperator {
  HasAny = "has_any",
  HasAll = "has_all",
  Matches = "matches",
}

export enum JobPostRequirementSeverity {
  Block = "block",
  Flag = "flag",
}

export enum JobPostRequirementReasonCode {
  MissingStateLicense = "missing_state_license",
  MissingProfessionalType = "missing_professional_type",
  MissingSpecialty = "missing_specialty",
  /** Used for the FreeText type only. Never emitted as a failure reason
   * since free_text rows always evaluate as satisfied — but the column is
   * notNull, so we need a value. */
  FreeText = "free_text",
}

/**
 * The stage of a professional's connection to a business unit, on
 * `organization_business_unit_affiliation`. `candidate` = in the pipeline (applied or
 * added/shared into the BU) but not hired; `hired` = active working relationship.
 */
export enum AffiliationStatus {
  Candidate = "candidate",
  Hired = "hired",
}

/**
 * How a professional's affiliation entered the pipeline —
 * `job-application` (applied to a post), `manual` (added/shared into the BU by an
 * org user), or `hire` (created directly as a hire).
 */
export enum AffiliationSource {
  JobApplication = "job-application",
  Manual = "manual",
  Hire = "hire",
  BuProviderInvite = "bu-provider-invite",
}

/**
 * How a provider's identity became visible to a business unit — recorded on
 * `organization_business_unit_provider_reveal`.
 *
 * Only `MarketplaceReveal` is metered by `monthly_provider_reveal_limit`. That
 * limit exists to cap how many *unknown* clinicians a BU can unmask from the
 * marketplace pool. Every other source records a provider the BU already knew
 * or who consented, so counting those would charge the BU for its own roster
 * (and let a single settings toggle exhaust a month's allowance).
 */
export enum ProviderRevealSource {
  /** Deliberate unmasking of a marketplace provider. Counts toward the limit. */
  MarketplaceReveal = "marketplace-reveal",
  /** The BU authored this person record itself, so nothing was disclosed. */
  RosterAdd = "roster-add",
  /** The provider accepted a roster invitation. */
  InvitationAccepted = "invitation-accepted",
  /** The provider shared their profile directly and authorized resharing. */
  DirectShare = "direct-share",
  /** Backfilled so already-affiliated providers stay visible when a BU turns the reveal gate on. */
  RevealGateEnabled = "reveal-gate-enabled",
}

/**
 * Why a professional's affiliation ended — recorded on
 * `organization_business_unit_affiliation_ended`. `resigned` is voluntary, `terminated` is
 * org-initiated; `rejected` / `withdrawn` end a candidacy (org- vs provider-driven).
 */
export enum AffiliationEndReason {
  MarkedInError = "marked_in_error",
  Resigned = "resigned",
  Terminated = "terminated",
  Rejected = "rejected",
  Withdrawn = "withdrawn",
}

/* ------------------------------------------------------------------------- *
 * Provider credentials redesign (docs/provider-credentials-data-model-design.md)
 * Tables are registered in schema/index.ts; migrations 0241 (tables) and
 * 0242 (credential_type seed) create them.
 * ------------------------------------------------------------------------- */

/** Credential kinds. Behavior (Zod schema for `details`, identity rule,
 * expirability, PSV support) lives in the TS credential-type registry, not the DB. */
export enum CredentialType {
  StateLicense = "state-license",
  Dea = "dea",
  Cds = "cds",
  BoardCertification = "board-certification",
  Ecfmg = "ecfmg",
  Education = "education",
  Training = "training",
  LifeSupport = "life-support",
  HealthScreening = "health-screening",
  BirthCertificate = "birth-certificate",
  Passport = "passport",
  DriversLicense = "drivers-license",
  GovernmentId = "government-id",
  MalpracticeInsurance = "malpractice-insurance",
  /** 5-year professional-liability claims/settlement history (NCQA-required;
   * distinct from proof of current coverage = malpractice-insurance). */
  MalpracticeClaimsHistory = "malpractice-claims-history",
  WorkHistory = "work-history",
  HospitalAffiliation = "hospital-affiliation",
  /** Clinical/procedural activity logs (case logs) — procedure-volume
   * evidence facilities require for privileging. Exists today as file
   * type 9. */
  CaseLog = "case-log",
  /** Signed provider application/attestation. NCQA requires it be current
   * (within 180 days of the credentialing decision) — a dated, immutable,
   * provider-signed version; packet sealing should require a current one. */
  Attestation = "attestation",
  /** Criminal background check artifact (e.g. Checkr report). A collected
   * document, not primary-source verified (TJC treats it as an artifact). */
  BackgroundCheck = "background-check",
  /** Clinical peer reference — one credential per referee (instance
   * identity via the registry's identity rule). */
  PeerReference = "peer-reference",
  /** Collaborative practice / supervising-physician agreement for mid-level
   * practitioners (NP/PA/CRNA); `details` links the supervising provider. */
  CollaborativeAgreement = "collaborative-agreement",
  Npi = "npi",
  MedicaidId = "medicaid-id",
  MedicareId = "medicare-id",
  /** Versioned demographics snapshot (legal name, aliases, DOB, address).
   * Live identity stays on `user`; profile edits generate versions one-way. */
  PersonalInfo = "personal-info",
  /** CV/résumé — document-classification type (has a parser today, file type 1). */
  Cv = "cv",
  Other = "other",
}

/** OCR lifecycle of a credential document (derived metadata — mutable in place). */
export enum CredentialDocumentOcrStatus {
  None = "none",
  Pending = "pending",
  Parsed = "parsed",
  Error = "error",
}

/** Who introduced a credential document. */
export enum CredentialDocumentOrigin {
  Provider = "provider",
  BusinessUnit = "business-unit",
  Import = "import",
}

/**
 * What the uploader asked the pipeline to do with a document.
 *
 * `classify` is the historical behaviour and stays the default: parse the file
 * and write what it finds straight into the professional's record, filling only
 * empty fields.
 *
 * `review` stages the parse for a person to confirm before anything is written.
 * Nothing about the professional's data changes until someone resolves the
 * candidate — see docs/credential-upload-review-design.md.
 */
export enum CredentialDocumentIntent {
  Classify = "classify",
  Review = "review",
}

/** Where a review candidate is in its lifecycle. Terminal once resolved. */
export enum CredentialCandidateStatus {
  Pending = "pending",
  Accepted = "accepted",
  Rejected = "rejected",
}

/**
 * What a reviewer decided. Distinct from the status: `accepted` and
 * `evidence-only` both leave a `status = accepted` row, because both are
 * affirmative outcomes, but only one mints a version.
 */
export enum CredentialCandidateResolution {
  /** Mint the version (as corrected) and attach the document. */
  Accepted = "accepted",
  /** Attach the document to the current holding; change no values. */
  EvidenceOnly = "evidence-only",
  /** Mint nothing, attach nothing; record the decision. */
  Rejected = "rejected",
}

/** Who supplied a credential version's structured data. Verification is a
 * separate event recorded in `credential_verification`; Baton is provenance,
 * not proof of primary-source verification. */
export enum CredentialVersionSource {
  Provider = "provider",
  Organization = "organization",
  Baton = "baton",
}

/** Owner kind of a credential view. */
export enum CredentialViewKind {
  Provider = "provider",
  BusinessUnit = "business-unit",
}

/** Lifecycle of a credential view. Ending an affiliation freezes its view;
 * rejoining creates a new view on the new affiliation episode. The only thaw is
 * reinstating that same organization-ended episode (`reinstate-affiliation.ts`). */
export enum CredentialViewStatus {
  Active = "active",
  Frozen = "frozen",
}

/** Lifecycle of a view holding. Holdings are append-only: repointing a view
 * marks the old row superseded and inserts a new active row. */
export enum CredentialHoldingStatus {
  Active = "active",
  Superseded = "superseded",
  Removed = "removed",
}

/** Lifecycle of a cross-view proposal. Dismissal is separate metadata and
 * leaves a current offer pending so it can still be imported later. */
export enum CredentialProposalStatus {
  Pending = "pending",
  Accepted = "accepted",
  Declined = "declined",
  Superseded = "superseded",
  Withdrawn = "withdrawn",
}

/** Display category grouping credential types on the provider/org UI. Backed
 * by the `credential_category` table (label + sort order live in the DB). */
export enum CredentialCategory {
  Licenses = "licenses",
  BoardCertifications = "board-certifications",
  Education = "education",
  Identity = "identity",
  Insurance = "insurance",
  Work = "work",
  /** Things done TO a person to clear them for work — a background check, a
   * health screening — as distinct from facts about who they are, which live
   * under Identity. Replaced the `personal-info` category in 0271_manual.sql. */
  Screenings = "screenings",
  Other = "other",
}

/** Whether a credential is required for a given professional type / BU.
 * `not-applicable` is distinct from `optional`: it means the credential does
 * not apply to this provider type at all (e.g. a residency certificate for a
 * PA) and must be excluded from completeness/"missing" counts. */
export enum CredentialRequirement {
  Required = "required",
  Optional = "optional",
  NotApplicable = "not-applicable",
}

/** Primary source category of a verification (mirrors NCQA/Assured source list). */
export enum CredentialVerificationType {
  Npi = "npi",
  Oig = "oig",
  Sam = "sam",
  Ofac = "ofac",
  MedicareOptOut = "medicare-opt-out",
  MedicaidExclusion = "medicaid-exclusion",
  StateLicense = "state-license",
  BoardCertification = "board-certification",
  DeaCds = "dea-cds",
  Npdb = "npdb",
  /** PSV of highest education (med school etc.). The primary source used
   * (e.g. "AMA Physician Profile", "ECFMG/EICS", registrar) goes in `source`. */
  Education = "education",
  /** PSV of residency/fellowship training (AMA profile, program office). */
  Training = "training",
  /** NCQA 5-year work-history review/verification. */
  WorkHistory = "work-history",
  /** Verification of current/prior hospital affiliations/privileges. */
  HospitalAffiliation = "hospital-affiliation",
  DocumentAuthenticity = "document-authenticity",
  Other = "other",
}

/** Verification types performed against the practitioner as a PERSON
 * (name/DOB/NPI), not against a specific credential: they stay valid when
 * unrelated credentials are edited, so they anchor to `provider_user_id`
 * alone — both `credential_version_id` and `document_id` NULL. Keep in sync
 * with `credential_verification_subject_check` in credentialVerification.ts. */
export const PERSON_SCOPED_VERIFICATION_TYPES = [
  CredentialVerificationType.Oig,
  CredentialVerificationType.Sam,
  CredentialVerificationType.Ofac,
  CredentialVerificationType.MedicareOptOut,
  CredentialVerificationType.MedicaidExclusion,
  CredentialVerificationType.Npdb,
] as const;

/** Outcome of a verification event. */
export enum CredentialVerificationResult {
  Clear = "clear",
  Concern = "concern",
  MissingInfo = "missing-info",
  Error = "error",
}

/** What KIND of actor performed a verification. */
export enum CredentialVerifiedCategory {
  Human = "human",
  Automated = "automated",
  Vendor = "vendor",
}

/** WHICH external verification service performed it (when verifier_kind =
 * 'vendor' or an automated check ran through a vendor's API). `verified_by_user_id`
 * covers the human case; this covers the service. Add a value per integration. */
export enum CredentialVerifierVendor {
  Assured = "assured",
  Baton = "baton",
}

/** Packet lifecycle. After `sealed`, items and header are immutable
 * (trigger-enforced); `voided` is the only later transition. */
export enum CredentialPacketStatus {
  Draft = "draft",
  Sealed = "sealed",
  Voided = "voided",
}

/** Overall packet outcome (Assured/Medallion-style). */
export enum CredentialPacketResult {
  Clear = "clear",
  Concern = "concern",
}

/** Kind discriminator for polymorphic packet items. */
export enum CredentialPacketItemKind {
  CredentialVersion = "credential-version",
  Document = "document",
  Verification = "verification",
}

/** Which legacy credential table a credential_legacy_map row points from.
 * Used by the double-write layer and the backfill so both converge on the
 * same new-model rows instead of duplicating them. */
export enum CredentialLegacyKind {
  /** user_credentials_file */
  File = "file",
  /** user_credentials_file_version */
  FileVersion = "file-version",
  /** user_credentials_field_value */
  FieldValue = "field-value",
  /** user_credentials_field_value_version */
  FieldValueVersion = "field-value-version",
  /** provider_credential_edit_request */
  EditRequest = "edit-request",
}

/**
 * Outcome-oriented lifecycle of one notification_campaign sequence instance.
 * `in_progress` is the only live state; the other five are terminal.
 * `dismissed`/`unreachable` BLOCK a restart while the user stays eligible
 * (they join `in_progress` in the partial unique index); the three OUTCOMES
 * (`succeeded` / `no_longer_eligible` / `completed_unsuccessful`) are assigned
 * from the campaign's `getState` hook at a wake-up and allow a fresh instance
 * if the user ever re-qualifies. Running the full schedule is recorded on
 * `completed_at`, orthogonal to the outcome.
 */
export enum NotificationCampaignStatus {
  /** Running — advancing through the schedule, or exhausted and awaiting outcome. */
  InProgress = "in_progress",
  /** Terminal: the user opted out of this campaign ("stop reminding me"). */
  Dismissed = "dismissed",
  /** Terminal: we could not communicate with the user (no deliverable channel). */
  Unreachable = "unreachable",
  /** Terminal: the campaign achieved its objective. */
  Succeeded = "succeeded",
  /** Terminal: the user stopped being applicable without achieving the objective (e.g. rejected/withdrawn). */
  NoLongerEligible = "no_longer_eligible",
  /** Terminal: EVERY message ran (schedule + grace) and the objective still wasn't achieved. */
  CompletedUnsuccessful = "completed_unsuccessful",
}
/**
 * Canonical meaning of a form field, used to autofill new forms from a
 * provider's past answers ("Cell Phone" and "Best number to reach you" both
 * mean phone-number). The set is human-owned: new codes are added here plus a
 * seed migration — the AI label classifier only ever maps labels onto
 * existing codes, never invents them.
 */
export enum FormFieldCategory {
  FullName = "full-name",
  FirstName = "first-name",
  MiddleName = "middle-name",
  LastName = "last-name",
  Email = "email",
  PhoneNumber = "phone-number",
  DateOfBirth = "date-of-birth",
  SocialSecurityNumber = "social-security-number",
  HomeAddress = "home-address",
  City = "city",
  State = "state",
  ZipCode = "zip-code",
  NpiNumber = "npi-number",
  DeaNumber = "dea-number",
  MedicalLicenseNumber = "medical-license-number",
  MedicalLicenseState = "medical-license-state",
  Specialty = "specialty",
  ProfessionalType = "professional-type",
  MedicalSchool = "medical-school",
  ResidencyProgram = "residency-program",
  BoardCertification = "board-certification",
  MalpracticeCarrier = "malpractice-carrier",
  MalpracticePolicyNumber = "malpractice-policy-number",
  EmergencyContactName = "emergency-contact-name",
  EmergencyContactPhone = "emergency-contact-phone",
  /** Sentinel: label was classified but fits no category. Re-swept when new categories are added. */
  Uncategorized = "uncategorized",
}

/**
 * Pluggable places form-fill suggestions can come from. The client sends an
 * ordered array of these with the suggestion queries — array order IS the
 * priority, and sources blend per field (each one only fills fields every
 * higher-priority source left empty).
 */
export enum FormSuggestionSource {
  PastSubmissions = "past-submissions",
  ProviderProfile = "provider-profile",
}

/**
 * Persisted provenance on `form_submission_value`, and every source the
 * engine can run. Clients pick from the narrower `FormSuggestionSource`, so
 * the AI fallback can never be requested or reordered by a client.
 */
export enum FormValueSuggestionSource {
  PastSubmissions = "past-submissions",
  ProviderProfile = "provider-profile",
  AiGenerated = "ai-generated",
}

/**
 * How a stored answer came to be. Human-asserted values outrank merely
 * accepted ones; NULL (pre-provenance and PDF rows) ranks with accepted.
 */
export enum FormSuggestionState {
  Typed = "typed",
  Accepted = "accepted",
  Edited = "edited",
  DismissedThenTyped = "dismissed-then-typed",
}

/**
 * The AI double-check on a suggestion. Three states because provider data
 * usually cannot speak to a value at all — a boolean would turn every such
 * case into a false veto or false reassurance. Only `disagree` acts: it
 * silently vetoes the suggestion.
 */
export enum AiAnswerVerdict {
  Agree = "agree",
  Disagree = "disagree",
  Unknown = "unknown",
}

/**
 * `fast` paints the form from deterministic sources alone; `complete` re-runs
 * them and appends the AI fallback for whatever they left empty.
 */
export enum FormAutofillPhase {
  Fast = "fast",
  Complete = "complete",
}

/** Whether a failing autofill source fails the request or is simply skipped. */
export enum FormAutofillFailureMode {
  Throw = "throw",
  Skip = "skip",
}

/**
 * How a billing plan's price row charges a category: a fixed price per unit
 * past the included quantity, or a percentage of each event's basis amount
 * (e.g. shift wages). See api/docs/organization-usage-billing-design.md.
 */
export enum BillingPricingMode {
  PerUnit = "per-unit",
  PercentOfBasis = "percent-of-basis",
}

export enum BillingPlanStatus {
  Active = "active",
  Scheduled = "scheduled",
  Ended = "ended",
}

/**
 * Primary keys of the organization_billing_category const table. Add a seeded
 * row and a recording path whenever a new category is introduced here.
 */
export enum OrganizationBillingCategoryCode {
  ProviderReveal = "provider-reveal",
  JobPost = "job-post",
  ProviderMonitoring = "provider-monitoring",
  DocumentSignature = "document-signature",
  ShiftMarkup = "shift-markup",
}

/**
 * Which stored object of a legacy credential file version to open. HEIC
 * originals also have a server-written JPEG beside them for browser previews.
 */
export enum CredentialFileRendition {
  Original = "original",
  Jpeg = "jpeg",
}

/**
 * How a PSV screening issue compares with the most recent earlier review item
 * that contained the same source record.
 */
export enum PsvReviewIssueStatus {
  /** No earlier item contained this source record. */
  New = "new",
  /** The same record, with different material fields or identity
   * comparisons. */
  Changed = "changed",
  /** The same record with the same material fields. */
  Unchanged = "unchanged",
  /** Unchanged, but the decision it relied on is 36 months old or older. */
  Expired = "expired",
}

/**
 * Which source priced a time entry, checked in this order: a custom wage on
 * the shift, then the job offer's wages, then the organization wage set version recorded
 * on an ad-hoc shift.
 */
export enum WageLevel {
  Custom = "custom",
  JobOffer = "job-offer",
  OrganizationWageSet = "organization-wage-set",
}

/** What the admin communications view can show for a delivered message. */
export enum CommunicationContentKind {
  Email = "email",
  Text = "text",
  Unavailable = "unavailable",
}
